import {
    InspectorControls,
} from '@wordpress/block-editor';
import {
    PanelBody,
    PanelRow,
    Button,
    Flex,
    FlexItem,
    __experimentalText as Text
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import React, { useState } from 'react';

interface InspectorControlsProps {
    attributes: {
        attachmentIds?: number[];
        attachmentUrls?: string[];
    };
    setAttributes: (attributes: Record<string, any>) => void;
}

const Inspector: React.FC<InspectorControlsProps> = ({
    attributes: { attachmentIds = [], attachmentUrls = [] },
    setAttributes,
}) => {
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const getFileName = (url: string) => {
        const parts = url.split('/');
        const filename = parts[parts.length - 1];
        return filename.split('?')[0];
    };

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            return;
        }

        const newIds = [...attachmentIds];
        const newUrls = [...attachmentUrls];

        // Entferne das Element von der ursprünglichen Position
        const draggedId = newIds.splice(draggedIndex, 1)[0];
        const draggedUrl = newUrls.splice(draggedIndex, 1)[0];

        // Füge das Element an der neuen Position ein
        newIds.splice(dropIndex, 0, draggedId);
        newUrls.splice(dropIndex, 0, draggedUrl);

        setAttributes({
            attachmentIds: newIds,
            attachmentUrls: newUrls,
        });

        setDraggedIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
    };

    const removeAttachment = (index: number) => {
        const newIds = [...attachmentIds];
        const newUrls = [...attachmentUrls];
        newIds.splice(index, 1);
        newUrls.splice(index, 1);
        setAttributes({
            attachmentIds: newIds,
            attachmentUrls: newUrls,
        });
    };

    const moveUp = (index: number) => {
        if (index === 0) return;

        const newIds = [...attachmentIds];
        const newUrls = [...attachmentUrls];

        // Tausche mit dem vorherigen Element
        [newIds[index], newIds[index - 1]] = [newIds[index - 1], newIds[index]];
        [newUrls[index], newUrls[index - 1]] = [newUrls[index - 1], newUrls[index]];

        setAttributes({
            attachmentIds: newIds,
            attachmentUrls: newUrls,
        });
    };

    const moveDown = (index: number) => {
        if (index === attachmentIds.length - 1) return;

        const newIds = [...attachmentIds];
        const newUrls = [...attachmentUrls];

        // Tausche mit dem nächsten Element
        [newIds[index], newIds[index + 1]] = [newIds[index + 1], newIds[index]];
        [newUrls[index], newUrls[index + 1]] = [newUrls[index + 1], newUrls[index]];

        setAttributes({
            attachmentIds: newIds,
            attachmentUrls: newUrls,
        });
    };

    if (attachmentIds.length === 0) {
        return null;
    }

    return (
        <InspectorControls>
            <PanelBody title={__('Downloads verwalten', 'neodigital')} initialOpen={true}>
                <Text as="p" style={{ marginBottom: '8px', fontSize: '11px', color: '#757575' }}>
                    {__('Ziehen zum Sortieren:', 'neodigital')}
                </Text>

                {attachmentIds.map((id, index) => (
                    <div key={id} style={{ marginBottom: '4px' }}>
                        <div
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: draggedIndex === index ? '2px dashed #007cba' : '1px solid #ddd',
                                borderRadius: '3px',
                                backgroundColor: draggedIndex === index ? '#f0f6fc' : '#fff',
                                cursor: 'move',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                transition: 'all 0.2s ease',
                                opacity: draggedIndex === index ? 0.8 : 1,
                                minHeight: '32px'
                            }}
                        >
                            <Flex align="center" style={{ flex: 1, minWidth: 0 }}>
                                <FlexItem style={{ marginRight: '6px', flexShrink: 0 }}>
                                    <span style={{ fontSize: '14px', color: '#999', lineHeight: 1 }}>⋮⋮</span>
                                </FlexItem>
                                <FlexItem style={{ flex: 1, minWidth: 0 }}>
                                    <Text style={{
                                        fontSize: '11px',
                                        wordBreak: 'break-all',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                        lineHeight: 1.2
                                    }}>
                                        {getFileName(attachmentUrls[index] || '')}
                                    </Text>
                                </FlexItem>
                            </Flex>
                        </div>
                    </div>
                ))}
            </PanelBody>
        </InspectorControls>
    );
};

export default Inspector;
